type HeadersFormType = 'entries' | 'instance' | 'record';

type HeadersForm<T extends HeadersFormType = HeadersFormType> = {
  entries: [string, string][];
  instance: Headers;
  record: Record<string, string>;
}[T];

export const getHeadersEntries = (headers?: HeadersInit): [string, string][] => {
  if (headers instanceof Headers) {
    return [...headers.entries()];
  }
  return Array.isArray(headers) ? headers : Object.entries(headers ?? {});
};

const HeaderTransformers: { [key in HeadersFormType]: (headers: HeadersInit) => HeadersForm<key> } =
  {
    entries: (headers: HeadersInit) => getHeadersEntries(headers),
    instance: (headers: HeadersInit) => new Headers(headers),
    record: (headers: HeadersInit) => Object.fromEntries(getHeadersEntries(headers)),
  };

const getHeadersFormType = (headers: HeadersInit): HeadersFormType => {
  if (headers instanceof Headers) {
    return 'instance';
  } else if (Array.isArray(headers)) {
    return 'entries';
  } else if (typeof headers === 'object') {
    return 'record';
  }
  throw new Error('Encountered invalid headers form!');
};

/**
 * Merges the provided header values, {@link HeaderInit[]}, into a single header value,
 * {@link HeaderInit}, with headers defined later in the sequence overriding headers defined earlier
 * in the sequence.
 */
export const mergeHeaders = <T extends HeadersInit>(
  headers: T,
  ...additional: (HeadersInit | undefined)[]
): T => {
  const filteredAdditional = additional.filter(h => h !== undefined);
  if (filteredAdditional.length === 0) {
    return headers;
  } else if (filteredAdditional.length === 1) {
    const newHeaders = new Headers(headers);
    const entries = getHeadersEntries(filteredAdditional[0]);
    for (const [key, value] of entries) {
      newHeaders.set(key, value);
    }
    return HeaderTransformers[getHeadersFormType(headers)](newHeaders) as T;
  }
  return filteredAdditional.reduce((acc: T, curr: HeadersInit) => mergeHeaders(acc, curr), headers);
};
